import { SOLUTIONS_NL } from '@energiebespaarders/constants';
import { Box, Flex, Icon, Image, theme } from '@energiebespaarders/symbols';
import { Small } from '@energiebespaarders/symbols/helpers';
import { useIsMobile } from '@energiebespaarders/symbols/hooks';
import { Star } from '@energiebespaarders/symbols/icons/solid';
import { padding } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { ImageZoomWrapper, StyledPostCard } from '../_blog/PostPreview';
import type { InstallerLandingReview } from './installerTestimonials';

const StyledImage = styled(Image)`
  width: 100%;
  transition: transform 0.15s ${theme.curves.sharp};
  border-radius: 6px 6px 0 0;
  height: 100%;
  object-fit: cover;
`;

const StyledImageZoomWrapper = styled(ImageZoomWrapper)<{ $height: string }>`
  &::after {
    background: linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.1));
  }
`;

const StyledCard = styled(StyledPostCard)`
  &:hover {
    ${StyledImage} {
      transform: scale(1.1);
    }

    ${StyledImageZoomWrapper} {
      &::after {
        background: none;
        opacity: 0;
      }
    }
  }
`;

const StyledPostTitle = styled.div`
  font-size: ${x => x.theme.type.scale[5]};
  font-weight: 500;
`;

const StyledQuote = styled.div`
  ${padding(3, 'x')}
  flex-grow: 1;
  font-size: ${x => x.theme.type.scale[6]};
`;

interface TestimonialCardProps {
  testimonial: InstallerLandingReview;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial }) => {
  const isMobile = useIsMobile();
  return isMobile ? (
    <StyledCard p={0} animation="none" borderRadius="6px" shadow="none" borderColor="grayLight">
      <Flex flexWrap="wrap">
        <Box width={[1 / 2, 1 / 3]}>
          <Image
            src={testimonial.imageUrl}
            alt={testimonial.name}
            style={{ borderRadius: '6px 0 0 6px' }}
          />
        </Box>
        <Box width={[1 / 2, 2 / 3]} px={3} py={2}>
          <StyledPostTitle>
            {testimonial.name}
            {testimonial.city ? `, ${testimonial.city}` : ''}
          </StyledPostTitle>
          {testimonial.solution && (
            <div>
              <Small>{SOLUTIONS_NL[testimonial.solution]}</Small>
            </div>
          )}
          {!!testimonial.rating &&
            [...new Array(testimonial.rating)].map((_, index) => (
              <Icon key={`star-${index}`} icon={Star} fill="green" pr="3px" />
            ))}
          <StyledQuote>"{testimonial.quote}"</StyledQuote>
        </Box>
      </Flex>
    </StyledCard>
  ) : (
    <StyledCard p={0} animation="none" borderRadius="6px" shadow="none" borderColor="grayLight">
      <StyledImageZoomWrapper $height="120px">
        <StyledImage src={testimonial.imageUrl} alt={testimonial.name} />
      </StyledImageZoomWrapper>
      <Box px={3} py={2} style={{ borderBottom: `1px solid ${theme.colors.grayLight}` }}>
        <StyledPostTitle>
          {testimonial.name}
          {testimonial.city ? `, ${testimonial.city}` : ''}
        </StyledPostTitle>
        {testimonial.solution && (
          <div>
            <Small>{SOLUTIONS_NL[testimonial.solution]}</Small>
          </div>
        )}
        {!!testimonial.rating &&
          [...new Array(testimonial.rating)].map((_, index) => (
            <Icon key={`star-${index}`} icon={Star} fill="green" pr="3px" />
          ))}
      </Box>
      <Box px={3} py={1}>
        <StyledQuote>"{testimonial.quote}"</StyledQuote>
      </Box>
    </StyledCard>
  );
};

export default TestimonialCard;
